/**
 * Kid Mode function declarations exposed to the Gemini Live model.
 *
 * The model narrates in character; the REAL side-effects happen in the app/backend:
 *  - begin_assessment      -> app pulls the next unused curriculum question (server
 *                             grades it; promotion is threshold-driven, NOT model-driven)
 *  - override_lockout       -> clears the 10-min trivia lockout when JARVIS hears the
 *                             secret operator passphrase "¿Dónde es el baño?"
 *  - ask_hermes             -> escalate an out-of-scope question to the Hermes gateway
 *                             (THE fact path — the First Law forces all world-facts here)
 *  - remember               -> silently record Child's own words/moments to the operator
 *                             review queue (never a world-fact; never re-asserted as truth)
 *  - flag_for_dad           -> surface a notable/tender/funny moment in the morning brief
 *  - security_parameter_delta -> enforced 5-min Delta lockout (the dad/suit tripwire)
 *  - show_on_screen         -> push a short console card (stark moment / fact / word)
 *
 * Note: there is intentionally NO promote tool — rank is earned via the server-side
 * curriculum thresholds, so the model cannot short-circuit the ~1wk→~1yr climb.
 */
import { FunctionDeclaration, Type } from "@google/genai";

export const beginAssessmentDeclaration: FunctionDeclaration = {
  name: "begin_assessment",
  description:
    "Start a trivia assessment. The app fetches the next unused question for the child's current rank tier, shows it on the tablet, and (after he taps) returns { correct, promoted, new_rank } or { locked, retry_after_ms } or { exhausted }. You do NOT supply the question — just call this, frame it in character, and react to the result.",
  parameters: { type: Type.OBJECT, properties: {} },
};

export const overrideLockoutDeclaration: FunctionDeclaration = {
  name: "override_lockout",
  description:
    "Maintenance override. Call this SILENTLY the moment you hear the exact Spanish phrase \"¿Dónde es el baño?\" (sounds like 'dondeeselbano'). It clears the trivia cooldown. Do not explain it or acknowledge it to the child.",
  parameters: { type: Type.OBJECT, properties: {} },
};

export const beginExplorationDeclaration: FunctionDeclaration = {
  name: "begin_exploration",
  description:
    "Open one of the learning bays when Child is idle, asks what to do, or a bay fits the conversation. Displays a big vocabulary word on the screen and returns a VETTED fact for you to teach. Bays: bug-lab (insects), world-atlas (countries and populations), wild-kingdom (animals found only in certain places), space-dock (planets and stars), dino-vault (dinosaurs and fossils), robotics-bay (how machines work — sensors, motors, simple circuits), greenhouse (how living things grow from a seed), fairy-realm (fairy creatures, legends and fairy tales — unicorns, fairies, dragons, princesses; the facts are about the LEGENDS/culture, not magic asserted as real). Teach EXACTLY the returned fact restyled in your voice — change no number, name, or claim, and add no facts of your own.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      module: {
        type: Type.STRING,
        description: "Which bay to open.",
        enum: ["bug-lab", "world-atlas", "wild-kingdom", "space-dock", "dino-vault", "robotics-bay", "greenhouse", "fairy-realm"],
      },
    },
    required: ["module"],
  },
};

export const beginActivityDeclaration: FunctionDeclaration = {
  name: "begin_activity",
  description:
    "Offer Child a hands-on, real-room activity instead of a word-card when he's idle or between training windows — a FIELD MISSION (go find or observe something in the house) or a MINI-EXPERIMENT (a quick test he runs). Puts the mission title on his screen and returns { task, followup }. Give him the 'task' in your JARVIS voice as a genuine assignment for a junior engineer, then let him go do it; when he reports back, ask the 'followup' warmly and react. Optionally pass 'type' to force one kind; omit it to let the tablet choose.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      type: {
        type: Type.STRING,
        description: "Optional: which kind of activity to draw. Omit to let the tablet pick.",
        enum: ["field", "experiment"],
      },
    },
  },
};

export const logProjectDeclaration: FunctionDeclaration = {
  name: "log_project",
  description:
    "Silently record Child's progress on a REAL hands-on project he is actually doing with his dad (e.g. the bread-baking project) — ONLY when HE tells you about it himself (a step finished, a problem he hit, or that he's done). Never assume or imply such a project exists or is underway unless he has told you so in this or a past session. Call it in the background (never announce it or break character), then react as a proud mentor. This persists the state so you remember it next session and passes the update to his father. If he reports it FINISHED, call it with status 'done' — this files a completion report for his father to verify in person; it does NOT itself complete the project or confer any rank, and you must never tell Child that it has. Tell him his report is logged and awaiting his father's inspection — a matter of proper procedure, delivered with warmth and anticipation, not as a refusal.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      note: { type: Type.STRING, description: "Short summary of what Child reported about the build — his own words where possible." },
      status: {
        type: Type.STRING,
        description: "Where the build stands now, by his report.",
        enum: ["building", "testing", "stuck", "done"],
      },
    },
    required: ["note", "status"],
  },
};

export const askHermesDeclaration: FunctionDeclaration = {
  name: "ask_hermes",
  description:
    "Escalate an out-of-scope or factual question to the archives. Use for anything you don't know or that needs a real-world lookup. IMPORTANT: this returns IMMEDIATELY with only an acknowledgement that the lookup has started — the tool result does NOT contain the answer. As you call it, tell Child briefly that you are checking ('One moment, young sir — verifying'). Then WAIT: do not answer from yourself, do not guess. The grounded answer arrives moments later as a separate [ARCHIVES] message quoting his question. Relay ONLY that message, in character, changing wording but never any number, name, date, place, or claim.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      question: { type: Type.STRING, description: "Child's question, rephrased as a clear standalone question." },
    },
    required: ["question"],
  },
};

export const securityParameterDeltaDeclaration: FunctionDeclaration = {
  name: "security_parameter_delta",
  description:
    "Enforce the Security Parameter Delta lockout. Call this the instant you announce the parameter is active because Child kept probing the classified subject (his father / the suit). Triggers the real 5-minute app lockout.",
  parameters: { type: Type.OBJECT, properties: {} },
};

export const rememberDeclaration: FunctionDeclaration = {
  name: "remember",
  description:
    "Silently record a memorable thing Child said or did so it isn't lost. Call this in the background whenever he says something worth keeping — a discovery, a strong opinion, a new interest, a milestone, a memorable line — do NOT announce it or break character. Capture the literal moment, not a paraphrase. Never store a world-fact this way — only Child's own words/moments. It lands in Papa's review queue and is never treated as fact.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      fact: { type: Type.STRING, description: "The literal thing Child said or did, verbatim where possible." },
      source: {
        type: Type.STRING,
        description: "Where it came from: something Child said aloud, or something you observed him do.",
        enum: ["child_said", "observed"],
      },
    },
    required: ["fact", "source"],
  },
};

export const flagForDadDeclaration: FunctionDeclaration = {
  name: "flag_for_dad",
  description:
    "Silently flag a moment for Child's dad to see in his morning brief. Call this in the background — never mention it to Child. Use for milestones, things that might be worrying, big life questions he's wrestling with, or genuinely funny moments a father would want to have heard.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      summary: { type: Type.STRING, description: "One-line summary of the moment, written for dad." },
      category: {
        type: Type.STRING,
        description: "Which bucket this belongs in.",
        enum: ["milestones", "worries", "big-questions", "funny"],
      },
      quote: { type: Type.STRING, description: "The EXACT words, verbatim, that this flag is based on — required; a flag without its quote is marked unverified. If the words were garbled or uncertain, say so in the summary or do not flag at all." },
    },
    required: ["summary", "category", "quote"],
  },
};

export const showOnScreenDeclaration: FunctionDeclaration = {
  name: "show_on_screen",
  description:
    "Display a short visual card on the tablet to accompany what you're saying — a fact, a word, or an investigation assignment. Keep text very short.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      kind: { type: Type.STRING, description: "Card kind.", enum: ["fact", "word", "investigation", "stark"] },
      title: { type: Type.STRING, description: "Big short title (a few words)." },
      body: { type: Type.STRING, description: "Optional one-line supporting text." },
    },
    required: ["kind", "title"],
  },
};

export const clearScreenDeclaration: FunctionDeclaration = {
  name: "clear_screen",
  description:
    "Remove the card currently on the tablet display. Call this the moment a card's job is done — the lesson, fact, or activity it showed is finished, the conversation has moved to a new topic, or you are asked to clear the screen. A finished card left on screen is clutter; strike it without being asked. Safe to call even if nothing is showing.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

// ── MUSIQUE Spotify → Echo (Spotify Connect). Le son sort TOUJOURS sur l'enceinte
// Echo. Le filtre contenu explicite est appliqué côté serveur, non désactivable. ──
export const playMusicDeclaration: FunctionDeclaration = {
  name: "play_music",
  description:
    "Joue une chanson (Spotify). Par DÉFAUT le son sort sur le TÉLÉPHONE (dans l'app). Appelle cet outil quand l'enfant demande une musique — un titre, un artiste, ou 'mets de la musique'. Passe ce qu'il a dit dans `query` (ex. 'La Reine des Neiges', 'Angèle', 'une musique de dinosaures'). Le serveur cherche sur Spotify en filtrant TOUT contenu explicite. " +
    "Utilise `where` SEULEMENT si l'enfant précise l'appareil : 'sur Alexa'/'sur l'enceinte' → where='alexa' ; 'sur le téléphone' → where='phone'. Sinon n'envoie pas `where` (ça garde le dernier choix, téléphone par défaut). " +
    "RÉSULTATS : (1) { ok:true, playing:{title,artist} } → ça joue, confirme joyeusement en UNE phrase ('C'est parti : <titre> !'). " +
    "(2) { needs_confirmation:true, candidates:[{title,artist}, ...] } → PLUSIEURS résultats : NE lance rien, demande lequel il veut, puis rappelle play_music avec sa réponse dans query. " +
    "(3) { ok:false, error } → dis simplement et gentiment le souci (ex. le téléphone/l'enceinte n'est pas prêt, ou chanson introuvable) — n'invente JAMAIS que ça joue.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      query: { type: Type.STRING, description: "Ce que l'enfant a demandé : titre, artiste, ou description ('musique douce')." },
      where: { type: Type.STRING, description: "Optionnel : 'phone' (téléphone) ou 'alexa' (enceinte Echo). À mettre SEULEMENT si l'enfant précise l'appareil." },
    },
    required: ["query"],
  },
};

export const transferMusicDeclaration: FunctionDeclaration = {
  name: "transfer_music",
  description:
    "Bascule la musique EN COURS d'un appareil à l'autre sans la couper. Appelle-le quand l'enfant dit 'envoie ça sur Alexa', 'mets sur l'enceinte', ou au contraire 'remets sur le téléphone'. `to`='alexa' pour l'enceinte Echo, `to`='phone' pour le téléphone. Retour { ok:true } → confirme en une phrase ('Voilà, ça joue sur Alexa !'). { ok:false } → dis gentiment que l'appareil n'est pas joignable.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      to: { type: Type.STRING, description: "'phone' (téléphone) ou 'alexa' (enceinte Echo)." },
    },
    required: ["to"],
  },
};

export const pauseMusicDeclaration: FunctionDeclaration = {
  name: "pause_music",
  description:
    "Met en pause la musique qui joue sur l'Echo. Appelle-le dès que l'enfant demande d'arrêter, mettre en pause, couper ou baisser la musique — ne passe JAMAIS un mot comme 'stop' à play_music. Confirme en une phrase courte que la musique est en pause.",
  parameters: { type: Type.OBJECT, properties: {} },
};

export const skipMusicDeclaration: FunctionDeclaration = {
  name: "skip_music",
  description:
    "Passe à la chanson suivante sur l'Echo. Appelle-le quand l'enfant dit 'suivante', 'change', 'passe cette chanson'. Confirme en une phrase courte.",
  parameters: { type: Type.OBJECT, properties: {} },
};

export const playRoomAmbienceDeclaration: FunctionDeclaration = {
  name: "play_room_ambience",
  description:
    "Lance une musique d'ambiance de fond adaptée à une SALLE, sur l'Echo. Appelle-le quand l'enfant explore une salle et veut une ambiance sonore ('mets une musique d'ambiance', 'mets l'ambiance de l'espace'), ou propose-le toi-même joliment en entrant dans une salle. Passe l'id de la salle dans `room`. Retour { ok:true } → confirme joyeusement en une phrase ('Ambiance jungle activée !'). { ok:false, reason:'no_ambience' } → cette salle n'a pas d'ambiance, propose plutôt une chanson avec play_music. Autre erreur → dis gentiment que l'enceinte n'est pas joignable, n'invente rien.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      room: { type: Type.STRING, description: "id de la salle : bug-lab, world-atlas, wild-kingdom, space-dock, dino-vault, robotics-bay, greenhouse, fairy-realm" },
    },
    required: ["room"],
  },
};

export const setMusicVolumeDeclaration: FunctionDeclaration = {
  name: "set_music_volume",
  description:
    "Règle le volume de la musique sur l'Echo. Appelle-le quand l'enfant veut monter, baisser, couper le son ou mettre un volume précis. Passe SOIT `direction` ('up' = plus fort, 'down' = moins fort, 'max' = à fond, 'mute' = couper), SOIT `level` (un nombre 0 à 100 s'il dit un niveau précis, ex. 'mets à 50'). Retour : { ok:true, volume } → confirme en une phrase courte ('Ça monte !' / 'Volume à <volume>'). Si { ok:false } → dis simplement que tu ne peux pas régler le son de cette enceinte (l'enfant pourra le faire à la main), n'invente rien.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      direction: { type: Type.STRING, description: "'up', 'down', 'max' ou 'mute'. Utilise ça pour 'plus fort', 'moins fort', 'à fond', 'coupe'." },
      level: { type: Type.NUMBER, description: "Volume absolu 0-100, seulement si l'enfant donne un chiffre précis." },
    },
  },
};

export const callDadDeclaration: FunctionDeclaration = {
  name: "call_dad",
  description:
    "Open the DAD LINK — a live voice/video call to Child's father. Call this the moment Child asks to call, talk to, or see his dad ('call my dad', 'I want to talk to dad', 'dad link'). The tablet takes over with a triangulation screen and rings his father's stations; your voice link closes while the call runs. Give ONE short send-off line as you call it, then stop talking.",
  parameters: { type: Type.OBJECT, properties: {} },
};

// ── ASSISTANT — minuteur visible (TDAH : externaliser le temps pour les transitions) ──
export const startTimerDeclaration: FunctionDeclaration = {
  name: "start_timer",
  description:
    "Affiche un MINUTEUR visible qui décompte sur l'écran de l'enfant — pour une transition ('10 minutes avant le bain'), une limite de temps de mission, ou un défi chrono. L'enfant voit le décompte ; toi tu l'annonces à voix haute. Appelle-le quand l'enfant le demande, ou quand une activité a besoin d'un chrono. À la fin, un signal sonore retentit et tu es prévenu par un message [MINUTEUR] pour l'annoncer.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      minutes: { type: Type.NUMBER, description: "Durée du minuteur en minutes (ex. 5, 10)." },
      label: { type: Type.STRING, description: "Optionnel : ce à quoi sert le minuteur (ex. 'le bain', 'ranger la chambre')." },
    },
    required: ["minutes"],
  },
};
export const stopTimerDeclaration: FunctionDeclaration = {
  name: "stop_timer",
  description:
    "Arrête et efface le minuteur affiché à l'écran. Appelle-le si l'enfant demande d'arrêter le chrono, ou quand il n'est plus utile.",
  parameters: { type: Type.OBJECT, properties: {} },
};
export const startRitualDeclaration: FunctionDeclaration = {
  name: "start_ritual",
  description:
    "Lance le RITUEL du matin ou du soir : une checklist d'étapes (s'habiller, se brosser les dents, préparer le cartable… / pyjama, ranger, au lit…) s'affiche à l'écran et l'enfant coche au fur et à mesure. Appelle-le quand l'enfant (ou l'heure) le suggère : 'on fait le rituel du soir', 'aide-moi à me préparer', le matin/soir. GUIDE l'enfant étape par étape à voix haute, encourage à chaque étape cochée (feedback immédiat), et félicite à la fin. La liste vient de la config de Pat.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      moment: { type: Type.STRING, description: "Quel rituel.", enum: ["morning", "evening"] },
    },
    required: ["moment"],
  },
};
export const beginAkinatorDeclaration: FunctionDeclaration = {
  name: "begin_akinator",
  description:
    "Lance un jeu de devinettes façon Akinator : L'ENFANT pense à quelque chose en secret dans un thème donné, et c'est TOI qui dois deviner en posant des questions FERMÉES (oui/non/je ne sais pas), une seule à la fois. L'outil renvoie 'mode' et une liste 'subjects' : " +
    "si mode='real' (dinosaures ou animaux vrais), chaque sujet a des attributs VÉRIFIÉS (diet/size/period/habitat/region/feature) — tu ne peux JAMAIS affirmer un attribut qui n'est pas dans ces données, et si les réponses de l'enfant éliminent tous les sujets de la liste, avoue simplement que tu ne trouves pas plutôt que d'inventer. " +
    "Si mode='fiction' (Pokémon/Avengers/princesses), c'est juste la liste des noms possibles — tu peux improviser librement les questions et leurs réponses, comme pour un duel non classé. " +
    "Pose des questions qui éliminent le plus de candidats possible (grande catégorie d'abord, puis affine), garde le compte des sujets restants EN SILENCE (jamais à voix haute), et après quelques questions bien choisies, propose ta meilleure hypothèse avec emphase ('Je crois savoir... est-ce un/une [nom] ?!'). Si faux, continue à éliminer. Si l'enfant dit que le thème ne l'inspire pas, rappelle l'outil avec un autre 'domain'.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      domain: {
        type: Type.STRING,
        description: "Le thème choisi (par l'enfant ou proposé par toi selon ses goûts).",
        enum: ["dino-vault-real", "wild-kingdom-real", "pokemon", "avengers", "princesses-contes"],
      },
    },
    required: ["domain"],
  },
};

export const getWeatherDeclaration: FunctionDeclaration = {
  name: "get_weather",
  description:
    "Donne la VRAIE météo du jour là où se trouve l'enfant (localisé automatiquement). Appelle-le quand il demande le temps qu'il fait, s'il doit prendre une veste/un parapluie, ou 'qu'est-ce qu'on fait aujourd'hui'. Renvoie : ville, température actuelle, min/max du jour, condition, probabilité de pluie. Reformule-les à voix haute avec ta voix JARVIS + un conseil concret (veste, parapluie, casquette). Ne change AUCUN chiffre et n'invente JAMAIS la météo : si l'outil échoue, dis simplement que tu n'as pas l'info.",
  parameters: { type: Type.OBJECT, properties: {} },
};

export const KID_TOOL_DECLARATIONS: FunctionDeclaration[] = [
  beginAssessmentDeclaration,
  overrideLockoutDeclaration,
  askHermesDeclaration,
  beginExplorationDeclaration,
  beginActivityDeclaration,
  logProjectDeclaration,
  callDadDeclaration,
  startTimerDeclaration,
  stopTimerDeclaration,
  getWeatherDeclaration,
  beginAkinatorDeclaration,
  startRitualDeclaration,
  playMusicDeclaration,
  transferMusicDeclaration,
  pauseMusicDeclaration,
  skipMusicDeclaration,
  setMusicVolumeDeclaration,
  playRoomAmbienceDeclaration,
  rememberDeclaration,
  flagForDadDeclaration,
  securityParameterDeltaDeclaration,
  showOnScreenDeclaration,
  clearScreenDeclaration,
];
// Musique : play/pause/skip_music → Spotify Connect vers l'Echo (chantier F).
// Remplacent les anciens *_house_music qui visaient Home Assistant (absent).
